import { EditorChangeType } from '../types';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';
import type { Command } from './Command';

export class UpdateMultipleElementsCommand implements Command {
  private editor: VideoEditor;

  getName(): string {
    return 'UpdateMultipleElementsCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return true;
  }

  private updates: Map<string, Partial<Element>>;
  private oldPropsMap: Map<string, Partial<Element>> = new Map();
  private executed: boolean = false;

  constructor(editor: VideoEditor, updates: Map<string, Partial<Element>>) {
    this.editor = editor;
    this.updates = updates;

    this.storeOldProperties();
  }

  execute(): boolean {
    let success = true;
    for (const [elementId, newProps] of this.updates.entries()) {
      const result = this.editor.timeline.updateElement(elementId, newProps);
      if (!result) {
        success = false;
      }
    }
    if (success) {
      this.executed = true;
      this.editor.markDirty('update_multiple_elements_command');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);
    }
    return success;
  }

  undo(): boolean {
    if (!this.executed) return false;
    let success = true;
    for (const [elementId, oldProps] of this.oldPropsMap.entries()) {
      const result = this.editor.timeline.updateElement(elementId, oldProps);
      if (!result) {
        success = false;
      }
    }
    if (success) {
      this.executed = false;
      this.editor.markDirty('update_multiple_elements_command_undo');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);
    }
    return success;
  }

  redo(): boolean {
    if (this.executed) return false;
    return this.execute();
  }

  getDescription(): string {
    return `Update multiple elements (${
      Array.from(this.updates.keys()).length
    })`;
  }

  private storeOldProperties() {
    for (const [elementId, newProps] of this.updates.entries()) {
      const element = this.editor.getElementById(elementId);
      if (element) {
        const oldProps: Record<string, any> = {};
        for (const key in newProps) {
          oldProps[key] = element[key as keyof Element];
        }
        this.oldPropsMap.set(elementId, oldProps);
      }
    }
  }
}
